import React, { FC, useCallback, useEffect } from "react";

import {
	Breadcrumb,
	Picto,
	Spinner,
	useMediaQuery,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useActiveProject } from "@/hooks/useActiveProject";
import { useActiveView } from "@/hooks/useActiveView";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { useProjectsOfView } from "@/hooks/useProjectsOfView";
import { useViews } from "@/hooks/useViews";

import { NewTaskButton } from "@/components/atoms";
import { ContextMenus } from "@/components/menus/ContextMenus";
import { BreadcrumbSelect, HeaderSearch } from "@/components/molecules";
import { HomeCalendar, IntroTour, TasksList } from "@/components/organisms";
import { PageLayout } from "@/components/templates";

import { setPageTitle } from "@/utils/UPageTitle";

import { MOBILE_MEDIA_QUERY } from "@/constants/CConstants";

import { TTask } from "@/types";

import { cn } from "@/utils";

import { HomePanel } from "./HomePanel";

export const Home: FC = () => {
	const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
	const { view, isUnknown: unknownView, isLoading } = useActiveView();
	const { isUnknown: unknownProject } = useActiveProject();
	const { taskId, isNewTask } = useHomeLocation();
	const { toView, toAll, toTask } = useHomeNavigation();

	useEffect(() => {
		if (view) setPageTitle(view.name);
	}, [view]);

	// The URL names a view or a project the page cannot show: it is
	// showing the fallback, so the address says so too (no back entry).
	useEffect(() => {
		if (!view) return;
		if (unknownView) toView(view.id, { replace: true });
		else if (unknownProject) toAll(view.id, { replace: true });
	}, [view, unknownView, unknownProject, toView, toAll]);

	const handleTaskClick = useCallback(
		(task: Pick<TTask, "id">) => toTask(task.id),
		[toTask]
	);

	const isPanelDisplayed = !!taskId || isNewTask;

	return (
		<PageLayout
			isLoading={isLoading}
			className={"flex flex-1 flex-col pr-0!"}
			wrapperClassName="pr-0! py-0!"
			scrollable={false}
			header={false}
		>
			<div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 pr-4">
				<div className="max-w-full shrink-0">
					<HomeBreadcrumb />
				</div>
				<div className="ml-auto flex flex-1 items-center justify-end gap-3">
					{!isMobile && <HeaderSearch />}
					{/* Mobile: the nav bar carries it. */}
					{!isMobile && <NewTaskButton />}
				</div>
			</div>
			<div className="-mt-1 flex flex-1 gap-8 overflow-hidden">
				<div
					className={cn([
						"mt-2 flex flex-1 flex-col gap-4 overflow-auto pr-1",
						"pr-4",
					])}
					id="home-tasks-list"
				>
					<HomeCalendar />
					<TasksList
						scroller=".dashboard__scrollable-cont-parent"
						onItemClick={handleTaskClick}
						selectable={true}
					/>
				</div>
				{isPanelDisplayed && (
					<div className="my-2 max-w-[50%] flex-1 empty:hidden">
						<HomePanel />
					</div>
				)}
			</div>
			<ContextMenus />
			<IntroTour />
		</PageLayout>
	);
};

const HomeBreadcrumb: React.FC = () => {
	const { view } = useActiveView();
	const { toAll } = useHomeNavigation();
	return (
		<Breadcrumb
			className="flex min-w-0 flex-wrap items-center gap-2"
			items={[
				{
					label: (
						<Picto
							icon="home"
							className="h-6 w-6"
							onClick={() => view && toAll(view.id)}
						/>
					),
				},
				{
					label: (
						<span data-tour="view">
							<ViewSelect />
						</span>
					),
				},
				{
					label: <ProjectSelect />,
				},
			]}
		/>
	);
};

const ViewSelect: React.FC = () => {
	const { views } = useViews();
	const { view } = useActiveView();
	const { toView } = useHomeNavigation();
	return (
		<BreadcrumbSelect
			// Archived views leave the switcher, unless it is the one shown.
			options={views
				.filter((cal) => !cal.isArchived || cal.id === view?.id)
				.map((cal) => ({
					label: cal.name,
					value: cal.id,
					color: cal.color,
				}))}
			value={view?.id ?? null}
			onChange={(value) =>
				toView(value === null ? undefined : Number(value))
			}
		/>
	);
};

const ProjectSelect: React.FC = () => {
	const { view } = useActiveView();
	const { project } = useActiveProject();
	const { toProject, toAll } = useHomeNavigation();
	const { projects, isLoading } = useProjectsOfView(view?.id);

	if (isLoading) {
		return <Spinner />;
	}

	return (
		<BreadcrumbSelect
			allLabel={t("projects.all")}
			// Archived projects stay reachable from their page, not from here.
			options={projects
				.filter((p) => !p.isArchived || p.id === project?.id)
				.map((p) => ({
					label: p.name,
					value: p.id,
					color: p.color,
				}))}
			value={project?.id ?? null}
			onChange={(value) => {
				if (!view) return;
				if (value === null) toAll(view.id);
				else toProject(Number(value));
			}}
		/>
	);
};
